/**
 * Inline-validation overlay model (OPSFL-CANVAS-1).
 *
 * The server compiler is the validation truth; the canvas only RENDERS its diagnostics. Two
 * honesty rules govern this module:
 *
 *  1. STALE-STATE HONESTY — a server verdict belongs to the definition it compiled. The moment the
 *     graph changes, the existing pipeline state already knows (`serverValidated` flips false — the
 *     same signal PipelineHint reads; no second dirty tracker). Rather than silently dropping the
 *     compiler's findings on the first keystroke, we keep the ones that still point at a live
 *     node/edge and mark them `stale` ("from last validation"), merged under the always-fresh
 *     client preview. Findings whose element was deleted vanish with it.
 *
 *  2. NO RE-IMPLEMENTATION — nothing here judges the flow. It only indexes, dedupes and formats
 *     what the compiler (or its faithful client preview subset) said.
 */
import type { UiDiagnostic } from '@/flow-ir/diagnostics';
import { edgeId, type FlowDefinition } from '@/flow-ir/types';

/** A UiDiagnostic plus the "from last validation" marker. Structural superset — indexes fine. */
export type DesignerDiagnostic = UiDiagnostic & { stale?: boolean };

/** Extra per-element render data the canvas components read off node/edge `data`. */
export interface DiagOverlay {
  /** Hover text: one line per finding, `SEVERITY CODE: message`. */
  diagTitle?: string;
  /** True when EVERY finding on the element is from the last (now out-of-date) validation. */
  diagStale?: boolean;
  /** True when the last server validation is current and found nothing on this element. */
  validated?: boolean;
}

const sig = (d: UiDiagnostic) => `${d.severity}|${d.code}|${d.nodeId ?? ''}|${d.edgeId ?? ''}`;

/**
 * Merge the fresh client preview with the last server validation's findings, marking the server
 * rows stale. Server findings anchored to elements that no longer exist are dropped; exact
 * duplicates of a preview finding are dropped (the fresh row wins).
 */
export function mergeWithLastValidation(
  preview: UiDiagnostic[],
  lastValidation: UiDiagnostic[] | null,
  def: FlowDefinition,
): DesignerDiagnostic[] {
  if (!lastValidation?.length) return preview;
  const nodeIds = new Set((def.nodes ?? []).map((n) => n.id));
  const edgeIds = new Set((def.edges ?? []).map((e) => e.id ?? edgeId(e)));
  const seen = new Set(preview.map(sig));
  const stale: DesignerDiagnostic[] = lastValidation
    .filter((d) => (d.nodeId ? nodeIds.has(d.nodeId) : d.edgeId ? edgeIds.has(d.edgeId) : true))
    .filter((d) => !seen.has(sig(d)))
    .map((d) => ({ ...d, stale: true }));
  return [...preview, ...stale];
}

/** Format the hover reveal for an element's findings (code + text, stale note when applicable). */
export function formatDiagTitle(list: DesignerDiagnostic[]): string {
  const lines = list.map((d) => `${d.severity.toUpperCase()} ${d.code}: ${d.message}`);
  if (list.length > 0 && list.every((d) => d.stale)) {
    lines.push('From the last validation — the flow changed since. Re-run Validate.');
  }
  return lines.join('\n');
}

/**
 * Compute the overlay for one element given its indexed findings. `serverValidated` is the live
 * pipeline flag (true only while the last compile still describes the current definition).
 */
export function overlayFor(
  list: DesignerDiagnostic[] | undefined,
  serverValidated: boolean,
): DiagOverlay | null {
  if (!list?.length) return serverValidated ? { validated: true } : null;
  return { diagTitle: formatDiagTitle(list), diagStale: list.every((d) => d.stale) };
}
